import type {
	ExerciseInfo,
	SectionPlanInfo,
	SectionPlanInfoId,
	SectionType,
	SectionTypeInput,
	StepPlanInfoId,
} from '@az3fr/core';
import { sectionTypes } from '@az3fr/core';
import { ChevronDownIcon, Clock, RotateCcw, Timer, Trash2Icon } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';

import { Editable } from '@/components/ui/Editable';
import { VirtualSelect } from '@/components/ui/virtual-select';
import { useExerciseDialog } from '@/entities/exercise/ui/use-exercise-dialog';
import { AddExerciseDrawerV2 } from '@/entities/exercise/ui/v2/add-exercise-drawer-v2';
import { sectionPlanSliceSelectors } from '@/entities/section-plan/model/section-plan.selector';
import { sectionTypeDisplayInfos } from '@/entities/section-plan/model/section-type';
import { stepPlanSliceSelectors } from '@/entities/step-plan/model/step-plan.selector';
import { workoutCreatorThunks } from '@/features/workout-creator/model';
import { StepEditorV2 } from '@/features/workout-creator/ui/v2/step-editor-v2';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';
import { type SelectOptionConfig, generateSectionTypeOptions } from '@/lib/option-generators';
import { cn } from '@/lib/utils';

function SectionPlanEditorV2(props: { sectionPlanId: SectionPlanInfoId; orderIndex: number }) {
	const { sectionPlanId, orderIndex } = props;
	const sectionPlan = useAppSelector((state) =>
		sectionPlanSliceSelectors.selectById(state, sectionPlanId),
	);
	const dispatch = useAppDispatch();
	const stepPlans = useAppSelector((state) =>
		sectionPlan ? stepPlanSliceSelectors.selectAllBySectionPlanId(state, sectionPlan.id) : [],
	);

	const handleExercisesAdded = useCallback(
		(exercises: ExerciseInfo[]) => {
			if (!sectionPlan) {
				return;
			}
			// Handles both step and set creation
			dispatch(
				workoutCreatorThunks.addStepPlans({
					sectionPlanId: sectionPlan.id,
					exerciseIds: exercises.map((exercise) => exercise.id),
				}),
			);
		},
		[dispatch, sectionPlan],
	);

	const handleStepDeleted = useCallback(
		(stepPlanId: StepPlanInfoId) => {
			// Handles cascading deletes
			dispatch(workoutCreatorThunks.removeStepPlan({ stepPlanId }));
		},
		[dispatch],
	);

	const {
		isOpen: isOpenAddExercise,
		open: openAddExercise,
		close: closeAddExercise,
		handleEntitiesSelect: onExercisesSelected,
	} = useExerciseDialog({ onExercisesSelected: handleExercisesAdded });

	if (!sectionPlan) {
		console.error('Section plan not found', sectionPlanId);
		return (
			<div className="border border-red-200 p-4">
				<p className="text-red-600">Error: Section not found</p>
			</div>
		);
	}

	return (
		<div key={sectionPlan.id}>
			{/* --- Section Header --- */}
			<SectionHeader sectionPlanId={sectionPlanId} orderIndex={orderIndex} />

			{/* --- Exercises Bar --- */}
			<div className="flex items-center justify-between border border-gray-200 border-t-0 bg-white px-4 py-2 sm:px-5 sm:py-2.5">
				<h2 className="font-medium text-gray-500 text-xs uppercase tracking-widest">
					Exercises ({stepPlans.length})
				</h2>
				<div className="flex items-center gap-1.5 sm:gap-2">
					<AddExerciseDrawerV2
						onSelect={onExercisesSelected}
						isOpen={isOpenAddExercise}
						close={closeAddExercise}
					/>
					<button
						type="button"
						className="flex h-7 items-center justify-center gap-1.5 rounded-md bg-gray-900 px-3 font-medium text-white text-xs transition-colors hover:bg-gray-800 disabled:opacity-50 sm:h-8 sm:px-4 sm:text-sm"
						onClick={openAddExercise}
					>
						+ Add
					</button>
				</div>
			</div>

			{/* --- Steps --- */}
			{stepPlans.map((stepPlan, index) => (
				<StepEditorV2
					orderIndex={index}
					key={stepPlan.id}
					stepPlanId={stepPlan.id}
					onStepDelete={handleStepDeleted}
				/>
			))}
		</div>
	);
}

function useSectionTypeInputs(sectionPlanId: SectionPlanInfoId): {
	sectionPlan: SectionPlanInfo | null;
	inputs: {
		name: SectionTypeInput;
		label: string;
		icon: React.ReactNode;
		displayValue: string;
		value: SelectOptionConfig<number> | null;
		onChange: (selectedOption: SelectOptionConfig<number> | null) => void;
		options: SelectOptionConfig<number>[];
		disabled: boolean;
		menuClassName: string;
	}[];
} {
	const dispatch = useAppDispatch();
	const sectionPlan = useAppSelector((state) =>
		sectionPlanSliceSelectors.selectById(state, sectionPlanId),
	);

	return useMemo(() => {
		if (!sectionPlan) {
			return { sectionPlan: null, inputs: [] };
		}

		const { sectionType, intervalSeconds } = sectionPlan;
		const sectionTypeKey: SectionType = sectionType;
		const displayInfo = sectionTypeDisplayInfos[sectionTypeKey];
		const { inputConfigs } = displayInfo;

		const inputs = inputConfigs.map((input) => {
			// @todo: this is a hack, we should fix the type of the sectionPlan
			const value: number = sectionPlan[input.column] as number;
			// If the section type is EMOM, and the interval is not set,
			// we disable the total duration input. (because it depends on the interval)
			const disabled =
				sectionType === sectionTypes.EMOM &&
				input.name === 'durationSeconds' &&
				intervalSeconds === null;

			// @todo: maybe do not use literal strings for input names?
			const isTimeCapOrInterval =
				(sectionType === sectionTypes.AMRAP && input.name === 'durationSeconds') ||
				(sectionType === sectionTypes.EMOM && input.name === 'intervalSeconds');
			const isEMOMDuration = sectionType === sectionTypes.EMOM && input.name === 'durationSeconds';

			const menuClassName = cn({
				'w-48': isEMOMDuration,
				'w-36': isTimeCapOrInterval,
			});

			const options = input.optionsGenerator(intervalSeconds);
			const onChange = (selectedOption: SelectOptionConfig | null) => {
				if (!selectedOption) {
					return;
				}

				const { value } = selectedOption;
				// For EMOM sections, changing the interval ('Every') invalidates the
				// 'Total Duration', as the duration must be a multiple of the interval.
				// To prevent an invalid state, we reset the duration to null,
				// forcing the user to select a new, valid duration.
				if (input.column === 'intervalSeconds') {
					dispatch(
						workoutCreatorThunks.updateSectionPlan({
							sectionPlanId: sectionPlan.id,
							changes: {
								intervalSeconds: value,
								durationSeconds: null, // Reset duration
							},
						}),
					);
				}

				// We need special handling for rounds because if any children step plan
				// is in 'explicit' repetition mode, we need to do some special logic.
				else if (input.column === 'rounds') {
					dispatch(
						workoutCreatorThunks.updateSectionPlanRounds({
							sectionPlanId: sectionPlan.id,
							newRounds: value,
						}),
					);
				} else {
					dispatch(
						workoutCreatorThunks.updateSectionPlan({
							sectionPlanId: sectionPlan.id,
							changes: { [input.column]: value },
						}),
					);
				}
			};

			const displayValue =
				value !== null && value !== undefined ? input.labelGenerator(value) : '-';

			const iconMap: Record<SectionTypeInput, React.ReactNode> = {
				rounds: <RotateCcw className="size-3.5 text-gray-700 sm:size-4" />,
				durationSeconds: <Clock className="size-3.5 text-gray-700 sm:size-4" />,
				intervalSeconds: <Timer className="size-3.5 text-gray-700 sm:size-4" />,
			};

			const inputName: SectionTypeInput = input.name;

			return {
				name: input.name,
				label: input.label,
				icon: iconMap[inputName],
				displayValue,
				value: options.find((option: SelectOptionConfig<number>) => option.value === value) ?? null,
				disabled,
				menuClassName,
				options,
				onChange,
			};
		});

		return { sectionPlan, inputs };
	}, [sectionPlan, dispatch]);
}

function SectionHeader(props: { sectionPlanId: SectionPlanInfoId; orderIndex: number }) {
	const { sectionPlanId, orderIndex } = props;
	const dispatch = useAppDispatch();
	const { sectionPlan, inputs } = useSectionTypeInputs(sectionPlanId);
	const sectionTypeOptions = generateSectionTypeOptions();
	const currentSectionTypeOption =
		sectionTypeOptions.find((option) => option.value === sectionPlan?.sectionType) ?? null;

	// @todo: we should think more about how to handle this kind of error
	if (!currentSectionTypeOption) {
		console.error(
			`Current section type option not found. this should not happen. please report this bug. sectionPlan: ${JSON.stringify(sectionPlan)}`,
		);
		return null;
	}

	// @todo: we should think more about how to handle this kind of error
	if (!sectionPlan) {
		console.error('Section plan not found', sectionPlanId);
		return (
			<div className="border border-red-200 p-4">
				<p className="text-red-600">Error: Section plan not found</p>
			</div>
		);
	}

	const { name } = sectionPlan;

	function handleSectionDelete() {
		// if (confirm('Are you sure you want to delete this section?')) {
		dispatch(workoutCreatorThunks.removeSectionPlan({ sectionPlanId }));
		// }
	}

	function handleSectionNameChange(value: string) {
		dispatch(workoutCreatorThunks.updateSectionPlan({ sectionPlanId, changes: { name: value } }));
	}

	const handleSectionTypeChange = (selectedOption: SelectOptionConfig<SectionType> | null) => {
		if (!selectedOption) {
			return;
		}
		const { value } = selectedOption;
		if (value !== sectionPlan?.sectionType) {
			dispatch(
				workoutCreatorThunks.switchSectionPlanType({
					sectionPlanId,
					newSectionType: value,
				}),
			);
		}
	};

	return (
		<header className="relative space-y-3 rounded-t-xl border border-gray-200 bg-white p-4 pb-3.5 sm:p-5 sm:pb-4">
			<div className="flex flex-col gap-2">
				<div className="flex items-center justify-between">
					{/* --- Section order --- */}
					<span className="pe-2 font-medium text-gray-500 text-xs uppercase tracking-widest">
						Section {orderIndex + 1}
					</span>
					{/* --- Section type and delete button --- */}
					<div className="flex items-center gap-2.5">
						<VirtualSelect
							options={sectionTypeOptions}
							value={currentSectionTypeOption}
							onChange={handleSectionTypeChange}
							enableVirtualization={false}
							nullable={false}
							renderDisplay={({ selectedOption }) => (
								<div className="inline-flex items-center gap-1.5 sm:gap-2">
									<span className="font-medium text-white text-xs sm:text-sm">
										{selectedOption?.label ?? 'Select...'}
									</span>
									<ChevronDownIcon className="size-3.5 text-white transition-transform group-data-open:rotate-180 sm:size-4" />
								</div>
							)}
							buttonClassName="group inline-flex h-7 cursor-pointer items-center justify-center rounded bg-green-500 px-2.5 font-medium text-white text-xs hover:bg-green-600 sm:h-8 sm:px-3 sm:text-sm"
							menuClassName="min-w-40 rounded-lg border border-gray-200 shadow-lg"
						/>
						<button
							type="button"
							className="group flex size-7 shrink-0 cursor-pointer items-center justify-center rounded bg-gray-100 hover:bg-red-50 sm:end-5 sm:top-5 sm:size-8"
							onClick={handleSectionDelete}
						>
							<Trash2Icon className="size-3.5 text-gray-500 group-hover:text-red-500 sm:size-4" />
						</button>
					</div>
				</div>
				<div className="flex flex-col gap-4">
					<div className="flex w-full flex-col gap-2">
						{/* ---- Section Name ---- */}
						<Editable
							// @todo: make sure this is fine using ''
							value={name ?? ''}
							onChange={handleSectionNameChange}
							placeholder="Warmup, Main Lift, Cool Down..."
							emptyValue="+ Add section name"
							valueButtonClassName="font-semibold text-base capitalize text-gray-900 sm:text-lg"
						/>
					</div>
				</div>
			</div>

			{/* --- Section configuration --- */}
			{inputs.length > 0 && (
				<div className="flex items-center justify-start gap-1.5 sm:gap-1.5">
					{inputs.map(
						({ options, icon, value, onChange, label, menuClassName, disabled, displayValue }) => (
							<VirtualSelect
								key={label}
								placeholder=" "
								enableVirtualization={true}
								options={options}
								value={value}
								onChange={onChange}
								renderDisplay={() => (
									<SectionConfigurationInputLabel
										label={label}
										icon={icon}
										displayValue={displayValue}
									/>
								)}
								disabled={disabled}
								buttonClassName="group flex h-7 items-center justify-center rounded-md bg-gray-100 px-2.5 font-medium text-gray-900 text-xs transition-all hover:bg-gray-200 sm:h-8 sm:px-3 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
								menuClassName={menuClassName}
							/>
						),
					)}
				</div>
			)}
		</header>
	);
}

function SectionConfigurationInputLabel(props: {
	label: string;
	icon: React.ReactNode;
	displayValue: string;
}) {
	const { label, icon, displayValue } = props;
	return (
		<div className="flex items-center gap-1.5 text-xs sm:gap-2 sm:text-sm">
			{icon}
			<span className="font-medium text-gray-600 capitalize">{label}:</span>
			<span className="font-bold text-gray-900">{displayValue}</span>
		</div>
	);
}

// Memoize the component to prevent unnecessary rerenders
const MemoizedSectionPlanEditor = memo(SectionPlanEditorV2);

export { MemoizedSectionPlanEditor as SectionPlanEditorV2 };
